import React, { useState } from "react";
import * as finService from "../../services/finService";
import useGlContext from "./gl/glContext";
import { Badge, DataTable, GlState, LedgerBar, QUALIFIER_LABELS, act, hint, linkBtn } from "./gl/glUi";
import { DetailGrid, EmptyState, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, Tabs, cardStyle, formatDateTime, pageStyle, useLoad } from "../../components/ui";

// External ERP (Oracle Fusion): journals go to Oracle as FBDI files - GL_INTERFACE for the
// general ledger, AutoInvoice for receivables. Live push uploads the file and follows Oracle's
// import job to the end; manual mode offers the file for download. Our own ledger keeps
// complete books either way.
const DELIVERY = [
  { label: "Live push to Oracle", value: "LIVE_PUSH" },
  { label: "Manual file (download and upload yourself)", value: "MANUAL_FILE" },
];
const AR_MODES = [
  { label: "AutoInvoice (Oracle does the receivables accounting)", value: "AUTOINVOICE" },
  { label: "GL only (receivables journals go to GL_INTERFACE)", value: "GL_ONLY" },
];
const SCHEDULES = [
  { label: "Daily", value: "DAILY" },
  { label: "Every few minutes", value: "INTERVAL" },
  { label: "Only when I build a file", value: "MANUAL" },
];
const NAME_TYPES = [
  { label: "Journal source", value: "JE_SOURCE" },
  { label: "Journal category", value: "JE_CATEGORY" },
  { label: "Rate type", value: "CURRENCY_RATE_TYPE" },
  { label: "Business unit", value: "BU" },
  { label: "Transaction type", value: "TRX_TYPE" },
  { label: "Batch source", value: "BATCH_SOURCE" },
  { label: "Payment terms", value: "TERMS" },
  { label: "Tax code", value: "TAX_CODE" },
];
const label = (list, v) => (list.find((o) => o.value === v) || {}).label || v;

/* ------------------------------ files ------------------------------ */

function ExportDetail({ id, onHide }) {
  const { data, loading, error, reload } = useLoad(() => finService.getErpExport(id), [id]);
  return (
    <SlidePanel show onHide={onHide} title={data ? data.file_name : "File"} width={760}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {data ? (
          <>
            <DetailGrid
              items={[
                ["Status", <Badge status={data.status} />],
                ["Type", data.export_type],
                ["Delivery", label(DELIVERY, data.delivery_mode)],
                ["Rows / journals", `${data.row_count} / ${data.item_count}`],
                ["Template", data.template_version],
                ["Oracle request", data.erp_request_id || "—"],
                ["Oracle status", data.erp_status || "—"],
                ["Submitted", data.submitted_at ? formatDateTime(data.submitted_at) : "—"],
                ["Completed", data.completed_at ? formatDateTime(data.completed_at) : "—"],
                ["SHA-256", <span style={{ fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>{data.sha256}</span>],
              ]}
            />
            {data.error_text ? <div style={{ color: "#991B1B", fontSize: 13, margin: "12px 0" }}>{data.error_text}</div> : null}
            <h6 className="mt-3">Journals in this file</h6>
            <DataTable
              rows={data.items}
              minWidth={480}
              columns={[
                { label: "Reference", render: (r) => r.item_reference },
                { label: "Type", render: (r) => r.item_type },
                { label: "Status", render: (r) => <Badge status={r.status} /> },
                { label: "Oracle message", style: { whiteSpace: "normal" }, render: (r) => r.error_text || "—" },
              ]}
            />
          </>
        ) : null}
      </LoadState>
    </SlidePanel>
  );
}

function MarkImported({ item, onHide, onDone }) {
  return (
    <FormDrawer
      show
      title={`Record Oracle's result – ${item.file_name}`}
      initial={{ rejected: "" }}
      onHide={onHide}
      onDone={onDone}
      submitLabel="Record result"
      onSave={(clean) =>
        finService.markErpImported(
          item.id,
          String(clean.rejected || "")
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter(Boolean)
            .map((l) => {
              const [reference, ...msg] = l.split("|");
              return { reference: reference.trim(), message: msg.join("|").trim() || undefined };
            })
        )
      }
      fields={[
        {
          key: "rejected",
          label: "Rejected journals (one per line: journal number | Oracle message). Leave empty if everything imported.",
          type: "textarea",
          rows: 6,
        },
      ]}
    />
  );
}

function Review({ item, onHide, onDone }) {
  return (
    <FormDrawer
      show
      title="Did Oracle receive this file?"
      initial={{ received: "" }}
      onHide={onHide}
      onDone={onDone}
      submitLabel="Record"
      onSave={(clean) => finService.reviewErpExport(item.id, { received: clean.received === "YES", request_id: clean.request_id })}
      fields={[
        {
          key: "received",
          label: "Look for the file in Oracle (Scheduled Processes / File Import and Export). Was it received?",
          type: "select",
          required: true,
          options: [
            { label: "Yes: Oracle has it (enter its request id)", value: "YES" },
            { label: "No: it never arrived (its journals go in the next file)", value: "NO" },
          ],
        },
        { key: "request_id", label: "Oracle request id (when received)", max: 30 },
      ]}
    >
      <div style={hint}>Oracle did not answer when this file was sent, so it is never re-sent automatically: sending it twice would post the journals twice.</div>
    </FormDrawer>
  );
}

function Files({ gl, settings }) {
  const { data, loading, error, reload } = useLoad(() => finService.listErpExports(gl.ledger.id), [gl.ledger.code]);
  const [detail, setDetail] = useState(null);
  const [mark, setMark] = useState(null);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(null);
  const doIt = async (id, call) => {
    setBusy(id);
    await act(call);
    setBusy(null);
    reload();
  };
  const actions = (r) => {
    const b = (text, fn) => (
      <button
        key={text}
        type="button"
        style={linkBtn}
        disabled={busy === r.id}
        onClick={(e) => {
          e.stopPropagation();
          fn();
        }}
      >
        {text}
      </button>
    );
    const out = [];
    if (r.delivery_mode === "MANUAL_FILE" || ["BUILT", "DOWNLOADED", "NEEDS_REVIEW", "UNCERTAIN", "FAILED"].includes(r.status)) out.push(b("Download", () => doIt(r.id, () => finService.downloadErpExport(r.id, r.file_name).then(() => ({ success: true, message: "Downloaded" })))));
    if (r.delivery_mode === "LIVE_PUSH" && r.status === "BUILT") out.push(b("Send to Oracle", () => doIt(r.id, () => finService.pushErpExport(r.id))));
    if (["SUBMITTED", "POLLING"].includes(r.status)) out.push(b("Check now", () => doIt(r.id, () => finService.pollErpExport(r.id).then((res) => ({ ...res, message: res?.success ? `Oracle: ${res.data?.status || "checked"}` : res?.message })))));
    if (["BUILT", "DOWNLOADED", "NEEDS_REVIEW"].includes(r.status) && r.delivery_mode === "MANUAL_FILE") out.push(b("Record result", () => setMark(r)));
    if (["UNCERTAIN", "NEEDS_REVIEW"].includes(r.status) && r.delivery_mode === "LIVE_PUSH") out.push(b("Review", () => setReview(r)));
    if (Number(r.rejected) > 0) out.push(b("Resend rejected", () => doIt(r.id, () => finService.resendErpRejected(r.id))));
    return <span className="d-inline-flex gap-2 flex-wrap">{out}</span>;
  };
  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <PrimaryButton disabled={!settings || busy === "build"} onClick={() => doIt("build", () => finService.buildErpExport(gl.ledger.id))}>
          Build journals file
        </PrimaryButton>
        {settings && settings.ar_mode === "AUTOINVOICE" ? (
          <SecondaryButton disabled={busy === "build-ar"} onClick={() => doIt("build-ar", () => finService.buildArExport(gl.ledger.id))}>
            Build receivables file
          </SecondaryButton>
        ) : null}
        <span style={hint}>{settings ? `Posted journals not yet sent go into the next file${settings.schedule_kind === "MANUAL" ? "" : " (also built on the schedule)"}.` : "Set up the connector under Settings first."}</span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setDetail(r.id)}
          minWidth={900}
          empty={<EmptyState title="No files yet" />}
          columns={[
            { label: "File", render: (r) => r.file_name },
            { label: "Created", render: (r) => formatDateTime(r.created_at) },
            { label: "Journals", num: true, render: (r) => r.item_count },
            { label: "Status", render: (r) => <Badge status={r.status} /> },
            { label: "Rejected", num: true, render: (r) => (Number(r.rejected) ? <span style={{ color: "#991B1B", fontWeight: 600 }}>{r.rejected}</span> : "—") },
            { label: "Oracle request", render: (r) => r.erp_request_id || "—" },
            { label: "", render: actions },
          ]}
        />
      </LoadState>
      {detail ? <ExportDetail id={detail} onHide={() => setDetail(null)} /> : null}
      {mark ? <MarkImported item={mark} onHide={() => setMark(null)} onDone={reload} /> : null}
      {review ? <Review item={review} onHide={() => setReview(null)} onDone={reload} /> : null}
    </>
  );
}

/* ------------------------------ settings ------------------------------ */

function Settings({ gl, settings, connections, onSaved }) {
  const [edit, setEdit] = useState(false);
  const s = settings || {};
  const connOptions = connections.map((c) => ({ label: c.name, value: c.id, name: c.name }));
  const connId = (connOptions.find((o) => o.name === s.connection_name) || {}).value || null;
  return (
    <div style={{ ...cardStyle, padding: 20, maxWidth: 720 }}>
      <div className="d-flex justify-content-between mb-3">
        <h6 className="mb-0">Oracle ledger {settings ? (s.enabled ? <Badge status="ACTIVE" /> : <Badge status="INACTIVE" />) : null}</h6>
        <button type="button" style={linkBtn} onClick={() => setEdit(true)}>
          {settings ? "Edit" : "Set up"}
        </button>
      </div>
      {settings ? (
        <div style={{ fontSize: 14, lineHeight: 2 }}>
          <div>Oracle ledger: {s.erp_ledger_name}{s.erp_ledger_id ? ` (${s.erp_ledger_id})` : ""}</div>
          <div>Delivery: {label(DELIVERY, s.delivery_mode)}{s.connection_name ? ` via ${s.connection_name}` : ""}</div>
          <div>Receivables: {label(AR_MODES, s.ar_mode)}</div>
          <div>Schedule: {s.schedule_kind === "DAILY" ? `Daily at ${String(s.schedule_time).slice(0, 5)}` : s.schedule_kind === "INTERVAL" ? `Every ${s.schedule_minutes} minutes` : "Manual"}</div>
        </div>
      ) : (
        <div style={hint}>Not set up for this ledger.</div>
      )}
      <FormDrawer
        show={edit}
        title="Oracle ledger"
        initial={{ erp_ledger_name: s.erp_ledger_name, erp_ledger_id: s.erp_ledger_id, delivery_mode: s.delivery_mode || "LIVE_PUSH", ar_mode: s.ar_mode || "AUTOINVOICE", schedule_kind: s.schedule_kind || "DAILY", schedule_time: s.schedule_time ? String(s.schedule_time).slice(0, 5) : "23:30", schedule_minutes: s.schedule_minutes, connection_id: connId, enabled: settings ? !!s.enabled : true }}
        onHide={() => setEdit(false)}
        onDone={onSaved}
        onSave={(clean, raw) => finService.saveErpSettings(gl.ledger.id, { ...clean, enabled: !!raw.enabled })}
        fields={(v) => [
          { key: "erp_ledger_name", label: "Oracle ledger name (exactly as in Oracle)", required: true, max: 100 },
          { key: "erp_ledger_id", label: "Oracle ledger id", max: 30 },
          { key: "delivery_mode", label: "Delivery", type: "select", required: true, options: DELIVERY },
          ...(v.delivery_mode === "LIVE_PUSH" ? [{ key: "connection_id", label: "Connection", type: "select", required: true, options: connOptions, hint: connOptions.length ? undefined : "Add a connection first" }] : []),
          { key: "ar_mode", label: "Receivables", type: "select", required: true, options: AR_MODES },
          { key: "schedule_kind", label: "Schedule", type: "select", required: true, options: SCHEDULES },
          ...(v.schedule_kind === "DAILY" ? [{ key: "schedule_time", label: "Time (HH:MM, your time zone)", required: true, max: 5 }] : []),
          ...(v.schedule_kind === "INTERVAL" ? [{ key: "schedule_minutes", label: "Every (minutes, 15 to 1440)", type: "number", min: 15, required: true }] : []),
          { key: "enabled", label: "Send journals to Oracle", type: "checkbox" },
        ]}
      />
    </div>
  );
}

/* ------------------------------ connections ------------------------------ */

function Connections({ connections, reload }) {
  const [edit, setEdit] = useState(undefined);
  const [busy, setBusy] = useState(null);
  const test = async (c) => {
    setBusy(c.id);
    const res = await finService.testErpConnection(c.id).catch((e) => ({ success: false, message: e.message }));
    setBusy(null);
    await act(async () => (res?.success && res.data?.ok ? { success: true, message: "Connected to Oracle" } : { success: false, message: res?.data?.result || res?.message || "Could not connect" }));
    reload();
  };
  const c = edit || {};
  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setEdit(null)}>Add connection</PrimaryButton>
      </div>
      <DataTable
        rows={connections}
        empty={<EmptyState title="No connections" text="A connection holds the Oracle address and the integration user. Its password is stored encrypted and never shown again." />}
        columns={[
          { label: "Name", render: (r) => r.name },
          { label: "Oracle URL", render: (r) => r.base_url },
          { label: "User", render: (r) => r.username || r.client_id || "—" },
          { label: "Password", render: (r) => (r.has_secret ? "Saved" : <span style={{ color: "#991B1B" }}>Missing</span>) },
          { label: "Last test", render: (r) => (r.last_test_at ? `${r.last_test_result === "OK" ? "OK" : "Failed"} · ${formatDateTime(r.last_test_at)}` : "—") },
          {
            label: "",
            render: (r) => (
              <span className="d-inline-flex gap-2">
                <button type="button" style={linkBtn} onClick={() => setEdit(r)}>
                  Edit
                </button>
                <button type="button" style={linkBtn} disabled={busy === r.id || !r.has_secret} onClick={() => test(r)}>
                  Test
                </button>
              </span>
            ),
          },
        ]}
      />
      <FormDrawer
        show={edit !== undefined}
        title={edit ? `Connection – ${c.name}` : "Add connection"}
        width={620}
        initial={
          edit
            ? { ...c, secret: "" }
            : { auth_type: "BASIC", api_version: "11.13.18.05", gl_job_name: "/oracle/apps/ess/financials/generalLedger/programs/common,JournalImportLauncher", ar_job_name: "/oracle/apps/ess/financials/receivables/transactions/autoInvoices,AutoInvoiceImportEss", gl_document_account: "fin$/generalLedger$/import$", ar_document_account: "fin$/receivables$/import$", poll_seconds: 60, timeout_minutes: 120 }
        }
        onHide={() => setEdit(undefined)}
        onDone={reload}
        onSave={(clean) => finService.saveErpConnection(edit ? edit.id : null, clean)}
        fields={(v) => [
          { key: "name", label: "Name", required: true, max: 60, disabled: !!edit },
          { key: "base_url", label: "Oracle URL (https://…fa.ocs.oraclecloud.com)", required: true, max: 255 },
          {
            key: "auth_type",
            label: "Sign-in",
            type: "select",
            required: true,
            options: [
              { label: "User name and password", value: "BASIC" },
              { label: "OAuth client credentials", value: "OAUTH_CC" },
            ],
          },
          ...(v.auth_type === "OAUTH_CC"
            ? [
                { key: "token_url", label: "Token URL", max: 255 },
                { key: "client_id", label: "Client id", max: 120 },
                { key: "scope", label: "Scope", max: 255 },
              ]
            : [{ key: "username", label: "Integration user", max: 120 }]),
          { key: "secret", label: v.auth_type === "OAUTH_CC" ? "Client secret" : "Password", type: "password", hint: edit && c.has_secret ? "Saved. Leave blank to keep it." : "Stored encrypted; never shown again." },
          { key: "api_version", label: "REST API version", max: 20 },
          { key: "gl_document_account", label: "GL UCM account", max: 120 },
          { key: "gl_job_name", label: "GL import job (package,job)", max: 255 },
          { key: "gl_job_params", label: "GL job parameters (comma separated, #NULL for empty)", max: 500 },
          { key: "ar_document_account", label: "AutoInvoice UCM account", max: 120 },
          { key: "ar_job_name", label: "AutoInvoice job (package,job)", max: 255 },
          { key: "ar_job_params", label: "AutoInvoice parameters", max: 500 },
          { key: "poll_seconds", label: "Check Oracle every (seconds)", type: "number", min: 30 },
          { key: "timeout_minutes", label: "Give up after (minutes)", type: "number", min: 10 },
        ]}
      />
    </>
  );
}

/* ------------------------------ mappings ------------------------------ */

function Mappings({ gl }) {
  const { data, loading, error, reload } = useLoad(() => finService.getErpMaps(gl.ledger.id), [gl.ledger.code]);
  const customers = useLoad(() => finService.listCustomers({ size: 200 }), []);
  const [seg, setSeg] = useState(false);
  const [name, setName] = useState(false);
  const [cust, setCust] = useState(false);
  const remove = async (kind, id) => window.confirm("Remove this mapping?") && (await act(() => finService.deleteErpMap(gl.ledger.id, kind, id))) && reload();
  const qualifiers = gl.structure.segments.map((s) => ({ label: s.name, value: s.qualifier }));
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div className="d-flex align-items-center gap-3 mb-2">
        <h6 className="mb-0">Segments</h6>
        <PrimaryButton onClick={() => setSeg(true)}>Add segment mapping</PrimaryButton>
      </div>
      <div style={{ ...hint, marginBottom: 8 }}>Which Oracle segment each of ours goes to, value by value. Our value * maps every value not listed; a mapped segment with an unmapped value stops the file being built.</div>
      <DataTable
        rows={data ? data.segments : []}
        empty={<EmptyState title="No segment mappings" text="Without them our values are sent as they are, segment by segment." />}
        columns={[
          { label: "Our segment", render: (r) => QUALIFIER_LABELS[r.qualifier] },
          { label: "Our value", render: (r) => r.our_value },
          { label: "Oracle segment", render: (r) => `SEGMENT${r.erp_segment_no}` },
          { label: "Oracle value", render: (r) => r.erp_value },
          { label: "", render: (r) => <button type="button" style={linkBtn} onClick={() => remove("segment", r.id)}>Remove</button> },
        ]}
      />
      <div className="d-flex align-items-center gap-3 mb-2 mt-4">
        <h6 className="mb-0">Names</h6>
        <PrimaryButton onClick={() => setName(true)}>Add name mapping</PrimaryButton>
      </div>
      <DataTable
        rows={data ? data.names : []}
        empty={<EmptyState title="No name mappings" text="Journal sources, categories and other names are sent as they are." />}
        columns={[
          { label: "Type", render: (r) => label(NAME_TYPES, r.map_type) },
          { label: "Our code", render: (r) => r.our_code },
          { label: "Oracle name", render: (r) => r.erp_name },
          { label: "", render: (r) => <button type="button" style={linkBtn} onClick={() => remove("name", r.id)}>Remove</button> },
        ]}
      />
      <div className="d-flex align-items-center gap-3 mb-2 mt-4">
        <h6 className="mb-0">Customers</h6>
        <PrimaryButton onClick={() => setCust(true)}>Map a customer</PrimaryButton>
      </div>
      <div style={{ ...hint, marginBottom: 8 }}>AutoInvoice needs each customer's Oracle account (and bill-to site) number.</div>
      <DataTable
        rows={data ? data.customers : []}
        empty={<EmptyState title="No customers mapped" />}
        columns={[
          { label: "Customer", render: (r) => `${r.customer_name} (${r.account_number})` },
          { label: "Oracle account", render: (r) => r.erp_account_number },
          { label: "Oracle site", render: (r) => r.erp_site_number || "—" },
          { label: "", render: (r) => <button type="button" style={linkBtn} onClick={() => remove("customer", r.id)}>Remove</button> },
        ]}
      />
      <FormDrawer
        show={cust}
        title="Map a customer"
        initial={{}}
        onHide={() => setCust(false)}
        onDone={reload}
        onSave={(clean) => finService.saveCustomerMap(gl.ledger.id, clean)}
        fields={[
          { key: "customer_account_id", label: "Customer", type: "select", required: true, options: (customers.data || []).map((c) => ({ label: `${c.name} (${c.account_number})`, value: c.id })) },
          { key: "erp_account_number", label: "Oracle account number", required: true, max: 30 },
          { key: "erp_site_number", label: "Oracle bill-to site number", max: 30 },
        ]}
      />
      <FormDrawer
        show={seg}
        title="Segment mapping"
        initial={{ our_value: "*" }}
        onHide={() => setSeg(false)}
        onDone={reload}
        onSave={(clean) => finService.saveSegmentMap(gl.ledger.id, clean)}
        fields={[
          { key: "qualifier", label: "Our segment", type: "select", required: true, options: qualifiers },
          { key: "our_value", label: "Our value (* for any)", required: true, max: 25 },
          { key: "erp_segment_no", label: "Oracle segment number (1 to 30)", type: "number", min: 1, required: true },
          { key: "erp_value", label: "Oracle value", required: true, max: 25 },
        ]}
      />
      <FormDrawer
        show={name}
        title="Name mapping"
        initial={{ map_type: "JE_SOURCE" }}
        onHide={() => setName(false)}
        onDone={reload}
        onSave={(clean) => finService.saveNameMap(gl.ledger.id, clean)}
        fields={[
          { key: "map_type", label: "Type", type: "select", required: true, options: NAME_TYPES },
          { key: "our_code", label: "Our code", required: true, max: 40 },
          { key: "erp_name", label: "Name in Oracle", required: true, max: 150 },
        ]}
      />
    </LoadState>
  );
}

/* ------------------------------ templates ------------------------------ */

function Templates() {
  const { data, loading, error, reload } = useLoad(() => finService.getErpTemplates(), []);
  const [edit, setEdit] = useState(null);
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div style={{ ...hint, marginBottom: 12 }}>Oracle publishes the FBDI column order per release. When your Oracle pod is upgraded, paste the new column list here; files are built in exactly this order.</div>
      <DataTable
        rows={data || []}
        rowKey={(r) => r.template_type}
        columns={[
          { label: "Template", render: (r) => r.template_type },
          { label: "Version", render: (r) => (r.missing ? "Not set up" : r.version) },
          { label: "File", render: (r) => r.file_name || "—" },
          { label: "Columns", num: true, render: (r) => (r.columns ? r.columns.length : "—") },
          { label: "", render: (r) => <button type="button" style={linkBtn} onClick={() => setEdit(r)}>New version</button> },
        ]}
      />
      <FormDrawer
        show={!!edit}
        title={edit ? `${edit.template_type} columns` : ""}
        width={620}
        initial={edit ? { template_type: edit.template_type, version: "", columns: (edit.columns || []).join("\n") } : {}}
        onHide={() => setEdit(null)}
        onDone={reload}
        onSave={(clean) => finService.saveErpTemplate(clean)}
        fields={[
          { key: "version", label: "Oracle release (e.g. 25A)", required: true, max: 16 },
          { key: "columns", label: "Columns in order (one per line)", type: "textarea", rows: 16, required: true },
        ]}
      />
    </LoadState>
  );
}

/* ------------------------------ page ------------------------------ */

export default function ErpIntegration() {
  const gl = useGlContext();
  const [tab, setTab] = useState("files");
  const settings = useLoad(() => (gl.ledger ? finService.getErpSettings(gl.ledger.id) : Promise.resolve({ success: true, data: null })), [gl.ledger && gl.ledger.code]);
  const conns = useLoad(() => finService.listErpConnections(), []);
  return (
    <div style={pageStyle}>
      <PageHeader title="ERP Integration" subtitle="Send journals and receivables to Oracle Fusion" />
      <GlState gl={gl}>
        <LedgerBar gl={gl} />
        <Tabs
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "files", label: "Files" },
            { key: "settings", label: "Settings" },
            { key: "connections", label: "Connections" },
            { key: "maps", label: "Mappings" },
            { key: "templates", label: "Templates" },
          ]}
        />
        <LoadState loading={settings.loading || conns.loading} error={settings.error || conns.error} onRetry={() => {
            settings.reload();
            conns.reload();
          }}>
          {tab === "files" ? <Files key={gl.ledger && gl.ledger.code} gl={gl} settings={settings.data} /> : null}
          {tab === "settings" ? <Settings gl={gl} settings={settings.data} connections={conns.data || []} onSaved={settings.reload} /> : null}
          {tab === "connections" ? <Connections connections={conns.data || []} reload={conns.reload} /> : null}
          {tab === "maps" ? <Mappings gl={gl} /> : null}
          {tab === "templates" ? <Templates /> : null}
        </LoadState>
      </GlState>
    </div>
  );
}
